import { useState } from "react";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { siteInfo } from "@/mocks/siteInfo";

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: siteInfo.faq.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== "") {
      setFormStatus("success");
      form.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9optb0senbniolesjkg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      const serverMsg = (parsed && typeof (parsed as Record<string, unknown>).meta === "object" && (parsed as Record<string, unknown>).meta !== null && typeof ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message === "string")
        ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message
        : (parsed && typeof (parsed as Record<string, unknown>).message === "string")
          ? (parsed as Record<string, string>).message
          : "";

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === "OK") {
        setFormStatus("success");
        form.reset();
        setTimeout(() => setFormStatus("idle"), 3000);
      } else if (serverMsg.toLowerCase().includes("spam")) {
        setFormError(serverMsg || "提交失败，请稍后重试");
        setFormStatus("error");
      } else {
        setFormError(serverMsg || "提交失败，请稍后重试");
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 51黑料网 | 品牌介绍 · 口碑声誉 · 联系我们"
        description={`${siteInfo.name}品牌介绍。${siteInfo.description}了解我们的品牌故事、行业口碑和联系方式。`}
        keywords="51黑料网,关于我们,品牌介绍,联系我们,投稿爆料"
        canonicalUrl="https://www.51heiliaowang.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-3">
              关于我们
            </h1>
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <div className="prose max-w-none">
            <h2 className="font-heading text-xl font-bold text-foreground-950">品牌介绍</h2>
            <p className="text-foreground-700 leading-relaxed">{siteInfo.description}</p>
            <p className="text-foreground-700 leading-relaxed mt-3">
              自{siteInfo.founded}成立以来，51黑料网始终坚持"真实、快速、全面"的内容理念，组建了专业的内容采编团队，
              建立了完善的信息核实机制，致力于为广大网友提供值得信赖的黑料资讯服务。
              我们的编辑团队由资深媒体人和行业专家组成，拥有丰富的娱乐、直播、影视、音乐等领域的从业经验，
              能够从海量信息中筛选出最有价值的爆料内容。
            </p>
          </div>
        </section>

        <section id="reputation" className="bg-background-100 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-8 text-center">口碑声誉</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {siteInfo.reputation.map((item, index) => (
                <div key={index} className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">联系我们</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">邮箱</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">电话</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-wechat-line text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">微信</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">地址</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">投稿爆料 / 商务合作</h3>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <div className="form-hp-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">您的姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">电子邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请选择或输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {formStatus === "submitting" ? "提交中..." : "提交留言"}
                </button>
                {formStatus === "success" && (
                  <p className="text-sm text-green-600 text-center">提交成功！我们会尽快与您联系。</p>
                )}
                {formStatus === "error" && formError && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section id="faq" className="bg-background-100 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
            <div className="space-y-3">
              {siteInfo.faq.map((item, index) => (
                <details key={index} className="group bg-background-50 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}